import { supabase } from '@/integrations/supabase/client'
import { z } from 'zod'

// Input validation schemas
export const emailSchema = z.string().email('Invalid email format').min(1, 'Email is required')
export const nameSchema = z.string().min(1, 'Name is required').max(100, 'Name too long')
export const phoneSchema = z.string().regex(/^[\+]?[1-9][\d]{0,15}$/, 'Invalid phone number').optional()
export const eventNameSchema = z.string().min(1, 'Event name is required').max(200, 'Event name too long')
export const descriptionSchema = z.string().max(1000, 'Description too long').optional()

// Guest validation schema
export const guestSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: phoneSchema
})

// Event validation schema
export const eventSchema = z.object({
  name: eventNameSchema,
  description: descriptionSchema,
  host_name: nameSchema,
  deadline: z.string().datetime().optional()
})

// Rate limiting function
export async function checkRateLimit(action: string, identifier?: string): Promise<boolean> {
  try {
    const response = await supabase.functions.invoke('rate-limiter', {
      body: { action, identifier }
    })

    if (response.error) {
      console.error('Rate limit check failed:', response.error)
      // For premium users, be more lenient on rate limit failures
      return true
    }

    return response.data?.allowed !== false // Allow if response is unclear
  } catch (error) {
    console.error('Rate limit error:', error)
    // On error, allow the request to proceed to avoid blocking legitimate users
    return true
  }
}

// Authorization helpers
export async function verifyEventAccess(eventId: string, userId: string): Promise<boolean> {
  try {
    const { data: event, error } = await supabase
      .from('events')
      .select('user_id, is_active')
      .eq('id', eventId)
      .single()

    if (error || !event) {
      return false
    }

    // User is event owner or event is active (for guests)
    return event.user_id === userId || event.is_active
  } catch (error) {
    console.error('Event access verification failed:', error)
    return false
  }
}

export async function verifyGuestAccess(guestId: string, eventId: string): Promise<boolean> {
  try {
    const { data: guest, error } = await supabase
      .from('event_guests')
      .select('id, event_id')
      .eq('id', guestId)
      .eq('event_id', eventId)
      .single()

    return !error && !!guest
  } catch (error) {
    console.error('Guest access verification failed:', error)
    return false
  }
}

// Input sanitization
export function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocols
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim()
}

// Secure token generation for guest links
export function generateSecureToken(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
}

// Log security events
export async function logSecurityEvent(
  eventType: string,
  eventDetails: Record<string, any> = {},
  userId?: string
): Promise<void> {
  try {
    await supabase.functions.invoke('security-logger', {
      body: {
        event_type: eventType,
        event_details: eventDetails,
        user_id: userId
      }
    })
  } catch (error) {
    console.error('Failed to log security event:', error)
  }
}

// File validation for uploads
export function validateFileUpload(file: File): { valid: boolean; error?: string } {
  const maxSize = 5 * 1024 * 1024 // 5MB
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 5MB' }
  }

  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Only JPEG, PNG, GIF, and WebP images are allowed' }
  }

  return { valid: true }
}